'use client';

import {
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { usePathname } from 'next/navigation';
import { useProjectSettings } from 'frontastic';
import {
  initializeTelemetry,
  isWebVitalsEnabled,
  trackPageView,
} from 'helpers/otel/documentLoad';
import { normalizeRoutePath } from 'helpers/otel/normalizeRoute';
import { initializeWebVitals } from 'helpers/otel/webVitals';
import { useFeatures } from 'providers/feature-flag';

const PROJECT_NAME =
  process.env.NEXT_PUBLIC_PROJECT_NAME ??
  'gmsb-frontend-components';

const ENVIRONMENT =
  process.env.NEXT_PUBLIC_APP_ENV ??
  process.env.NODE_ENV ??
  'unknown';

// Fallback keeps first-party classification correct until OTEL_FIRST_PARTY_HOSTS is set in every project.
const DEFAULT_FIRST_PARTY_HOSTS = [
  'fujifilm.com',
  'frontastic.io',
  'stripe.com',
  'stripe.network',
  'mediacliphub.com',
  'fujifilmapi.com'
];

export function SetupOTelSDK() {
  const pathname = usePathname();

  const { projectSettings } =
    useProjectSettings();

  const lastTrackedPage =
    useRef<string | null>(null);

  const otelTraces =
    projectSettings?.otelTraces as
    | string
    | undefined;

  const otelMetrics =
    projectSettings?.otelMetrics as
    | string
    | undefined;

  const otelLogs =
    projectSettings?.otelLogs as
    | string
    | undefined;

  const otelLogWarn = projectSettings?.otelLogWarn as
    | boolean
    | undefined;

  const otelLogInfo = projectSettings?.otelLogInfo as
    | boolean
    | undefined;

  const otelLogError = projectSettings?.otelLogError as
    | boolean
    | undefined;

  const otelWebVitals = projectSettings?.otelWebVitals as
    | boolean
    | undefined;

  const otelPageLoadMetrics = projectSettings?.otelPageLoadMetrics as
    | boolean
    | undefined;

  const otelMetricExportIntervalMs = projectSettings?.otelMetricExportIntervalMs as
    | number
    | undefined;

  const otelTraceSampleRate = projectSettings?.otelTraceSampleRate as
    | number
    | undefined;

  const otelEnvironment = projectSettings?.otelEnvironment as
    | string
    | undefined;

  const otelRegion = projectSettings?.otelRegion as
    | string
    | undefined;

  const otelFirstPartyHosts =
    projectSettings?.otelFirstPartyHosts;

  const firstPartyHosts = useMemo(() => {
    const configured = Array.isArray(
      otelFirstPartyHosts,
    )
      ? otelFirstPartyHosts
      : (otelFirstPartyHosts ?? '').split(
        /[,\s]+/,
      );

    const hosts = configured
      .map(host => host.trim())
      .filter(Boolean);

    return hosts.length
      ? hosts
      : DEFAULT_FIRST_PARTY_HOSTS;
  }, [otelFirstPartyHosts]);

  const { enableLgtm } = useFeatures();
  const telemetryEnabled = enableLgtm();

  useEffect(() => {
    if (
      !telemetryEnabled ||
      !otelTraces ||
      !otelMetrics ||
      !otelLogs
    ) {
      return;
    }

    initializeTelemetry(
      otelTraces,
      otelMetrics,
      otelLogs,
      Boolean(otelLogInfo),
      Boolean(otelLogError),
      Boolean(otelLogWarn),
      {
        projectName: PROJECT_NAME,
        serviceName:
          'gmsb-frontend-app',
        serviceNamespace:
          'frontend',
        environment:
          otelEnvironment || ENVIRONMENT,
        region:
          otelRegion,
        metricExportIntervalMillis:
          otelMetricExportIntervalMs,
        traceSampleRate:
          otelTraceSampleRate,
        enableWebVitals:
          Boolean(otelWebVitals),
        enablePageLoadMetrics:
          Boolean(otelPageLoadMetrics),
        tracePropagationUrls: [
          window.location.origin,
        ],
        skipRequestUrls: [
          /\/api\/auth(?:\/|$)/i,
          /\/auth(?:\/|$)/i,
          /\/login(?:\/|$)/i,
          /\/logout(?:\/|$)/i,
          /\/session(?:\/|$)/i,
          /\/token(?:\/|$)/i,
          /\/oauth(?:\/|$)/i,
          /\/oidc(?:\/|$)/i,
          /\/sso(?:\/|$)/i,
          /\/callback(?:\/|$)/i,
        ],
        firstPartyHosts: firstPartyHosts,
        apiRouteNormalizer: url =>
          normalizeRoutePath(
            url.pathname,
          ),
      },
    );

    if (isWebVitalsEnabled()) {
      initializeWebVitals({
        project: PROJECT_NAME,
        environment:
          otelEnvironment || ENVIRONMENT,
        region:
          otelRegion ?? 'unknown',
        service: 'gmsb-frontend-app',
        namespace: 'frontend',
      });
    }
  }, [
    telemetryEnabled,
    otelTraces,
    otelMetrics,
    otelLogs,
    otelLogInfo,
    otelLogError,
    otelLogWarn,
    otelWebVitals,
    otelPageLoadMetrics,
    otelMetricExportIntervalMs,
    otelTraceSampleRate,
    otelEnvironment,
    otelRegion,
    firstPartyHosts,
  ]);

  useEffect(() => {
    if (
      !telemetryEnabled ||
      !pathname
    ) {
      return;
    }

    const normalizedPath =
      normalizeRoutePath(pathname);

    const routeName =
      getRouteName(pathname);

    const pageKey =
      `${routeName}:${normalizedPath}`;

    if (
      lastTrackedPage.current ===
      pageKey
    ) {
      return;
    }

    lastTrackedPage.current =
      pageKey;

    trackPageView(
      normalizedPath,
      routeName,
    );
  }, [
    pathname,
    telemetryEnabled,
  ]);

  return null;
}

function getRouteName(
  pathname: string,
): string {
  if (
    pathname === '/' ||
    pathname === ''
  ) {
    return 'Home';
  }

  if (
    pathname === '/deals' ||
    pathname.startsWith('/deals/')
  ) {
    return 'Deals';
  }

  if (
    pathname === '/discover' ||
    pathname.startsWith(
      '/discover/',
    )
  ) {
    return 'Discover';
  }

  if (
    pathname === '/checkout' ||
    pathname.startsWith(
      '/checkout/',
    )
  ) {
    return 'Checkout';
  }

  if (
    pathname === '/cart' ||
    pathname.startsWith('/cart/')
  ) {
    return 'Cart';
  }

  if (
    pathname === '/search' ||
    pathname.startsWith(
      '/search/',
    )
  ) {
    return 'Search';
  }

  if (
    pathname.startsWith(
      '/products/',
    ) ||
    pathname.startsWith(
      '/product/',
    )
  ) {
    return 'Product Details';
  }

  if (
    pathname.startsWith(
      '/categories/',
    ) ||
    pathname.startsWith(
      '/category/',
    )
  ) {
    return 'Category';
  }

  if (
    pathname === '/account' ||
    pathname.startsWith(
      '/account/',
    )
  ) {
    return 'Account';
  }

  return normalizeRoutePath(pathname)
    .split('/')
    .filter(Boolean)
    .map(segment =>
      segment
        .replace(/[-_]/g, ' ')
        .replace(
          /\b\w/g,
          character =>
            character.toUpperCase(),
        ),
    )
    .join(' / ');
}